import { useEffect, useRef } from 'react';

type UseTabLazyLoadOptions<TKey> = {
  /**
   * The value that identifies the data to load (e.g., device's ID, inmate's ID, etc).
   * When this changes, the loaded state is reset.
   */
  key: TKey | null | undefined;
  /**
   * Whether the tab is currently selected.
   * When true for the first time, triggers the initial load.
   */
  tabSelected?: boolean;
  /**
   * Optional refresh trigger value.
   * When this changes and the tab was previously loaded, triggers a refresh.
   */
  refreshTrigger?: number;
  /**
   * The execute function to call when loading/refreshing data.
   * Should accept the key as a parameter.
   */
  execute: (key: TKey) => void;
};

/**
 * A reusable hook for lazy loading tab data and handling refresh triggers.
 *
 * Behavior:
 * - Loads data only when the tab is first selected (lazy loading)
 * - Refreshes data when refreshTrigger changes, but only if the tab was previously loaded
 * - Resets loaded state when the key changes
 *
 * @example
 * ```tsx
 * const dataHook = useSomeData(null);
 * useTabLazyLoad({
 *   key: deviceId,
 *   tabSelected: isTabSelected,
 *   refreshTrigger: refreshCounter,
 *   execute: dataHook.execute
 * });
 * // Access loading state directly from dataHook.loading
 * ```
 */
export function useTabLazyLoad<TKey>(options: UseTabLazyLoadOptions<TKey>): void {
  const { key, tabSelected, refreshTrigger, execute } = options;

  const lastRefreshTriggerRef = useRef<number | undefined>(undefined);
  const lastKeyRef = useRef<TKey | null | undefined>(undefined);
  const hasLoadedRef = useRef(false);
  const executeRef = useRef(execute);
  const lastExecutedKeyRef = useRef<TKey | null | undefined>(undefined);

  // Keep execute function ref up to date
  useEffect(() => {
    executeRef.current = execute;
  }, [execute]);

  // Reset loaded state when key changes
  useEffect(() => {
    if (key !== lastKeyRef.current) {
      lastKeyRef.current = key;
      hasLoadedRef.current = false;
      lastRefreshTriggerRef.current = undefined;
      lastExecutedKeyRef.current = undefined;
    }
  }, [key]);

  // Single effect to handle both initial load and refresh scenarios
  useEffect(() => {
    if (!key) {
      return;
    }

    // Scenario 1: Tab is first selected
    if (tabSelected && !hasLoadedRef.current) {
      // Prevent duplicate execution for the same key
      if (lastExecutedKeyRef.current === key) {
        return;
      }
      hasLoadedRef.current = true;
      lastExecutedKeyRef.current = key;
      executeRef.current(key);
      return;
    }

    // Scenario 2: Refresh trigger changed and tab was previously loaded
    if (
      refreshTrigger !== undefined &&
      refreshTrigger !== lastRefreshTriggerRef.current &&
      hasLoadedRef.current
    ) {
      lastRefreshTriggerRef.current = refreshTrigger;
      lastExecutedKeyRef.current = key;
      executeRef.current(key);
    }
  }, [tabSelected, refreshTrigger, key]);
}
